import { useState } from "react";
import { Settings2 } from "lucide-react";
import { useTaxStore, useCurrentTaxInput } from "../store/useTaxStore";
import { DeductionKey } from "../types/tax.types";
import type { TaxYearInput } from "../types/tax.types";
import { cn } from "@/lib/utils";
import AmountInput from "@/components/AmountInput";

const FIELD_GROUPS: Array<{
  section: string;
  fields: Array<{ key: DeductionKey; label: string }>;
}> = [
  {
    section: "Personal & Family",
    fields: [
      { key: DeductionKey.HasSpouse, label: "Non-income spouse" },
      { key: DeductionKey.ChildrenCount, label: "Children" },
      { key: DeductionKey.ParentSupportCount, label: "Parent support" },
      {
        key: DeductionKey.DisabledSupportCount,
        label: "Disabled/elderly support",
      },
    ],
  },
  {
    section: "Insurance & Savings",
    fields: [
      { key: DeductionKey.LifeInsurance, label: "Life Insurance" },
      { key: DeductionKey.HealthInsurance, label: "Health Insurance (self)" },
      {
        key: DeductionKey.ParentHealthInsurance,
        label: "Parent Health Insurance",
      },
      { key: DeductionKey.Sso, label: "Social Security (SSO)" },
    ],
  },
  {
    section: "Retirement Funds",
    fields: [
      { key: DeductionKey.Pvd, label: "Provident Fund (PVD)" },
      { key: DeductionKey.Rmf, label: "RMF" },
      { key: DeductionKey.Ssf, label: "SSF" },
      { key: DeductionKey.Tesg, label: "TESG (Thai ESG Fund)" },
    ],
  },
  {
    section: "Housing & Loans",
    fields: [
      { key: DeductionKey.HomeLoanInterest, label: "Home Loan Interest" },
    ],
  },
  {
    section: "Donations",
    fields: [
      {
        key: DeductionKey.EducationDonation,
        label: "Education / Sport Donation",
      },
      {
        key: DeductionKey.PoliticalDonation,
        label: "Political Party Donation",
      },
    ],
  },
];

function NumberInput({
  label,
  sublabel,
  value,
  onChange,
  readOnly,
}: {
  label: string;
  sublabel?: string;
  value: number;
  onChange?: (v: number) => void;
  readOnly?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-medium text-foreground leading-snug">
          {label}
        </div>
        {sublabel && (
          <div className="text-[12px] text-muted-foreground mt-0.5">
            {sublabel}
          </div>
        )}
      </div>
      <div className="relative flex-shrink-0">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-muted-foreground pointer-events-none">
          ฿
        </span>
        <AmountInput
          value={value ? String(value) : ""}
          readOnly={readOnly}
          onChange={(v) => onChange?.(Number(v) || 0)}
          placeholder="0"
          className={cn(
            "w-36 pl-7 pr-3 py-2 text-right text-[14px] rounded-lg border transition-colors",
            "font-sans [font-variant-numeric:tabular-nums]",
            "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
            "bg-card border-border text-foreground",
            "focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary",
            readOnly && "bg-muted cursor-default text-muted-foreground",
          )}
        />
      </div>
    </div>
  );
}

function CountInput({
  label,
  sublabel,
  value,
  onChange,
  max,
}: {
  label: string;
  sublabel?: string;
  value: number;
  onChange: (v: number) => void;
  max?: number;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-medium text-foreground leading-snug">
          {label}
        </div>
        {sublabel && (
          <div className="text-[12px] text-muted-foreground mt-0.5">
            {sublabel}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => onChange(Math.max(0, value - 1))}
          className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors text-lg leading-none"
        >
          −
        </button>
        <span className="w-6 text-center text-[15px] font-semibold text-foreground font-sans [font-variant-numeric:tabular-nums]">
          {value}
        </span>
        <button
          onClick={() => onChange(Math.min(max ?? 99, value + 1))}
          className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors text-lg leading-none"
        >
          +
        </button>
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  sublabel,
  checked,
  onChange,
}: {
  label: string;
  sublabel?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-medium text-foreground leading-snug">
          {label}
        </div>
        {sublabel && (
          <div className="text-[12px] text-muted-foreground mt-0.5">
            {sublabel}
          </div>
        )}
      </div>
      <button
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative w-11 h-6 rounded-full transition-colors flex-shrink-0",
          checked ? "bg-primary" : "bg-muted",
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform",
            checked && "translate-x-5",
          )}
        />
      </button>
    </div>
  );
}

function SectionHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="pt-4 pb-1">
      <div className="text-[12px] font-semibold text-muted-foreground uppercase tracking-wide">
        {title}
      </div>
      {subtitle && (
        <div className="text-[12px] text-muted-foreground mt-0.5">
          {subtitle}
        </div>
      )}
    </div>
  );
}

export default function DeductionsSection() {
  const input = useCurrentTaxInput();
  const { updateInput, enabledDeductions, toggleDeduction, viewYear } =
    useTaxStore();
  const [customizing, setCustomizing] = useState(false);
  const is2025 = viewYear === 2025;

  const isEnabled = (key: DeductionKey) => enabledDeductions.includes(key);

  function handleToggle(key: DeductionKey) {
    if (isEnabled(key)) {
      if (key === DeductionKey.HasSpouse) {
        updateInput("hasSpouse", false);
      } else {
        updateInput(key as keyof TaxYearInput, 0 as never);
      }
    }
    toggleDeduction(key);
  }

  const personalVisible = [
    DeductionKey.HasSpouse,
    DeductionKey.ChildrenCount,
    DeductionKey.ParentSupportCount,
    DeductionKey.DisabledSupportCount,
  ].some(isEnabled);
  const insuranceVisible = [
    DeductionKey.LifeInsurance,
    DeductionKey.HealthInsurance,
    DeductionKey.ParentHealthInsurance,
    DeductionKey.Sso,
  ].some(isEnabled);
  const retirementVisible = [
    DeductionKey.Pvd,
    DeductionKey.Rmf,
    DeductionKey.Ssf,
    DeductionKey.Tesg,
  ].some(isEnabled);
  const housingVisible =
    isEnabled(DeductionKey.HomeLoanInterest) ||
    (is2025 && isEnabled(DeductionKey.NewHomeEReceipt));
  const donationsVisible =
    (is2025 && isEnabled(DeductionKey.EasyEReceipt)) ||
    isEnabled(DeductionKey.EducationDonation) ||
    isEnabled(DeductionKey.PoliticalDonation);

  return (
    <div className="rounded-[14px] border border-border bg-card shadow-sm">
      <div className="px-5 pt-5 pb-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-[15px] font-semibold text-foreground">
              Deductions
            </h2>
            <p className="text-[12px] text-muted-foreground mt-0.5">
              Enter all allowances you are eligible for this tax year
            </p>
          </div>
          <button
            onClick={() => setCustomizing((v) => !v)}
            className={cn(
              "flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-medium transition-colors flex-shrink-0 mt-0.5",
              customizing
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-muted",
            )}
          >
            <Settings2 className="w-3.5 h-3.5" />
            {customizing ? "Done" : "Customize"}
          </button>
        </div>
      </div>

      {/* Customize panel */}
      {customizing && (
        <div className="mx-5 mt-3 mb-1 rounded-[10px] border border-border bg-muted/30 p-4">
          <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Visible fields
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
            {FIELD_GROUPS.map((group) => (
              <div key={group.section} className="mb-3">
                <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                  {group.section}
                </div>
                {group.fields.map((f) => (
                  <label
                    key={f.key}
                    className="flex items-center gap-2 py-0.5 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={isEnabled(f.key)}
                      onChange={() => handleToggle(f.key)}
                      className="w-3.5 h-3.5 accent-primary"
                    />
                    <span className="text-[12px] text-foreground">
                      {f.label}
                    </span>
                  </label>
                ))}
                {group.section === "Housing & Loans" && is2025 && (
                  <label className="flex items-center gap-2 py-0.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isEnabled(DeductionKey.NewHomeEReceipt)}
                      onChange={() =>
                        handleToggle(DeductionKey.NewHomeEReceipt)
                      }
                      className="w-3.5 h-3.5 accent-primary"
                    />
                    <span className="text-[12px] text-foreground">
                      New Home e-Receipt 2025
                    </span>
                  </label>
                )}
                {group.section === "Donations" && is2025 && (
                  <label className="flex items-center gap-2 py-0.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isEnabled(DeductionKey.EasyEReceipt)}
                      onChange={() => handleToggle(DeductionKey.EasyEReceipt)}
                      className="w-3.5 h-3.5 accent-primary"
                    />
                    <span className="text-[12px] text-foreground">
                      Easy e-Receipt 2025
                    </span>
                  </label>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="px-5 divide-y divide-border">
        {personalVisible && (
          <div>
            <SectionHeader title="Personal & Family" />
            {isEnabled(DeductionKey.HasSpouse) && (
              <ToggleRow
                label="Non-income spouse"
                sublabel="Allowance ฿60,000"
                checked={input.hasSpouse}
                onChange={(v) => updateInput("hasSpouse", v)}
              />
            )}
            {isEnabled(DeductionKey.ChildrenCount) && (
              <CountInput
                label="Number of children"
                sublabel="฿30,000 each"
                value={input.childrenCount}
                onChange={(v) => updateInput("childrenCount", v)}
              />
            )}
            {isEnabled(DeductionKey.ParentSupportCount) && (
              <CountInput
                label="Parent support (อุปการะบิดามารดา)"
                sublabel="฿30,000 each (max 4 parents)"
                value={input.parentSupportCount}
                onChange={(v) => updateInput("parentSupportCount", v)}
                max={4}
              />
            )}
            {isEnabled(DeductionKey.DisabledSupportCount) && (
              <CountInput
                label="Disabled / elderly support"
                sublabel="฿60,000 each"
                value={input.disabledSupportCount}
                onChange={(v) => updateInput("disabledSupportCount", v)}
              />
            )}
          </div>
        )}

        {insuranceVisible && (
          <div>
            <SectionHeader
              title="Insurance & Savings"
              subtitle="Life + health insurance combined cap ฿100,000"
            />
            {isEnabled(DeductionKey.LifeInsurance) && (
              <NumberInput
                label="Life Insurance Premium"
                sublabel="Up to ฿100,000 (combined with health insurance)"
                value={input.lifeInsurance}
                onChange={(v) => updateInput("lifeInsurance", v)}
              />
            )}
            {isEnabled(DeductionKey.HealthInsurance) && (
              <NumberInput
                label="Health Insurance Premium (self)"
                sublabel="Up to ฿25,000 (counts toward ฿100,000 cap)"
                value={input.healthInsurance}
                onChange={(v) => updateInput("healthInsurance", v)}
              />
            )}
            {isEnabled(DeductionKey.ParentHealthInsurance) && (
              <NumberInput
                label="Parent Health Insurance Premium"
                sublabel="Up to ฿15,000"
                value={input.parentHealthInsurance}
                onChange={(v) => updateInput("parentHealthInsurance", v)}
              />
            )}
            {isEnabled(DeductionKey.Sso) && (
              <NumberInput
                label="Social Security Fund (SSO)"
                sublabel="Up to ฿9,000"
                value={input.sso}
                onChange={(v) => updateInput("sso", v)}
              />
            )}
          </div>
        )}

        {retirementVisible && (
          <div>
            <SectionHeader
              title="Retirement Funds"
              subtitle="PVD + RMF + SSF combined cap ฿500,000 / TESG separate ฿300,000"
            />
            {isEnabled(DeductionKey.Pvd) && (
              <NumberInput
                label="Provident Fund (PVD)"
                sublabel="Up to 15% of salary (combined cap ฿500,000)"
                value={input.pvd}
                onChange={(v) => updateInput("pvd", v)}
              />
            )}
            {isEnabled(DeductionKey.Rmf) && (
              <NumberInput
                label="RMF (Retirement Mutual Fund)"
                sublabel="Up to 30% of income, max ฿500,000 (combined cap)"
                value={input.rmf}
                onChange={(v) => updateInput("rmf", v)}
              />
            )}
            {isEnabled(DeductionKey.Ssf) && (
              <NumberInput
                label="SSF (Super Savings Fund)"
                sublabel="Up to 30% of income, max ฿200,000 (combined cap)"
                value={input.ssf}
                onChange={(v) => updateInput("ssf", v)}
              />
            )}
            {isEnabled(DeductionKey.Tesg) && (
              <NumberInput
                label="TESG (Thai ESG Fund)"
                sublabel="Up to 30% of income, max ฿300,000 (separate cap)"
                value={input.tesg}
                onChange={(v) => updateInput("tesg", v)}
              />
            )}
          </div>
        )}

        {housingVisible && (
          <div>
            <SectionHeader title="Housing & Loans" />
            {isEnabled(DeductionKey.HomeLoanInterest) && (
              <NumberInput
                label="Home Loan Interest"
                sublabel="Up to ฿100,000"
                value={input.homeLoanInterest}
                onChange={(v) => updateInput("homeLoanInterest", v)}
              />
            )}
            {is2025 && isEnabled(DeductionKey.NewHomeEReceipt) && (
              <NumberInput
                label="New Home e-Receipt 2025"
                sublabel="Up to ฿100,000 (2025 only)"
                value={input.newHomeEReceipt}
                onChange={(v) => updateInput("newHomeEReceipt", v)}
              />
            )}
          </div>
        )}

        {donationsVisible && (
          <div>
            <SectionHeader title="Donations" />
            {is2025 && isEnabled(DeductionKey.EasyEReceipt) && (
              <NumberInput
                label="Easy e-Receipt 2025 (amount paid)"
                sublabel="2× deduction, max ฿100,000 (2025 only)"
                value={input.easyEReceipt}
                onChange={(v) => updateInput("easyEReceipt", v)}
              />
            )}
            {isEnabled(DeductionKey.EducationDonation) && (
              <NumberInput
                label="Education / Sport Donation (amount paid)"
                sublabel="2× deduction, max 10% of net taxable income"
                value={input.educationDonation}
                onChange={(v) => updateInput("educationDonation", v)}
              />
            )}
            {isEnabled(DeductionKey.PoliticalDonation) && (
              <NumberInput
                label="Political Party Donation"
                sublabel="Up to ฿10,000"
                value={input.politicalDonation}
                onChange={(v) => updateInput("politicalDonation", v)}
              />
            )}
          </div>
        )}
      </div>

      <div className="h-4" />
    </div>
  );
}
